import { useEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth/AuthContext';
import { useDebouncedValue } from '../../lib/hooks/useDebouncedValue';
import { Modal } from './Modal';
import {
  listAdminUsers,
  userAction,
  type AdminUserRow,
  type AdminUserList,
  type UserAction,
  type UserStatusValue,
  type UserTypeValue,
} from '../../lib/api/admin';
import { Pagination } from './Pagination';
import { UserDetailView } from './UserDetailView';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const ACTION_META: Record<
  UserAction,
  { title: string; verb: string; hint: string; needsReason: boolean; tone: 'danger' | 'accent' | 'neutral' }
> = {
  disable: {
    title: 'Disable account',
    verb: 'Disable',
    hint: 'Blocks sign-in entirely and revokes their sessions. Reverse with Enable.',
    needsReason: true,
    tone: 'danger',
  },
  enable: {
    title: 'Re-enable account',
    verb: 'Enable',
    hint: 'Restores sign-in for a disabled account.',
    needsReason: false,
    tone: 'neutral',
  },
  suspend: {
    title: 'Suspend account',
    verb: 'Suspend',
    hint: 'They can still sign in but cannot play; hidden from the leaderboard. Reverse with Reinstate.',
    needsReason: true,
    tone: 'danger',
  },
  reinstate: {
    title: 'Reinstate account',
    verb: 'Reinstate',
    hint: 'Lifts a suspension so they can play again.',
    needsReason: false,
    tone: 'neutral',
  },
  promote: {
    title: 'Promote to admin',
    verb: 'Promote',
    hint: 'Grants admin portal access.',
    needsReason: false,
    tone: 'accent',
  },
  demote: {
    title: 'Remove admin access',
    verb: 'Demote',
    hint: 'Removes admin portal access and signs them out.',
    needsReason: false,
    tone: 'danger',
  },
};

/**
 * Users tab — search/filter the user list and run moderation actions.
 * Hard-disable + suspend require a reason; promote is gated server-side to
 * @infinitymarketing.com (the row's `promotable` flag mirrors that so we only
 * surface the control when it'll succeed). Self-targeting destructive actions
 * are hidden (also blocked server-side).
 */
/** Optional sub-view by userId. When provided, the tab renders the detail
 *  view for that user with a Back link that calls `onSelect(null)`. */
export interface UsersTabProps {
  selectedUserId: string | null;
  onSelect: (userId: string | null) => void;
}

export function UsersTab({ selectedUserId, onSelect }: UsersTabProps) {
  if (selectedUserId) {
    return <UserDetailView userId={selectedUserId} onBack={() => onSelect(null)} />;
  }
  return <UsersList onSelect={onSelect} />;
}

function UsersList({ onSelect }: { onSelect: (userId: string) => void }) {
  const auth = useAuth();
  const selfId = auth.user?.userId;

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [statusFilter, setStatusFilter] = useState<UserStatusValue | ''>('');
  const [typeFilter, setTypeFilter] = useState<UserTypeValue | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [reloadKey, setReloadKey] = useState(0);

  const [data, setData] = useState<AdminUserList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pending, setPending] = useState<{ user: AdminUserRow; action: UserAction } | null>(null);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    listAdminUsers(
      {
        search: search || undefined,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      },
      { signal: controller.signal },
    )
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(
          err instanceof ApiError ? `Couldn't load users (${err.status}).` : "Couldn't load users.",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [search, statusFilter, typeFilter, page, pageSize, reloadKey]);

  // Stops a moderation action on the last row from stranding you on empty page N.
  useEffect(() => {
    if (!data || data.total === 0) return;
    const totalPages = Math.max(1, Math.ceil(data.total / pageSize));
    setPage((cur) => (cur > totalPages ? totalPages : cur));
  }, [data, pageSize]);

  const total = data?.total ?? 0;
  const users = data?.users ?? [];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
          Search
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Email or name"
            className="w-64 rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
          Status
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as UserStatusValue | '');
              setPage(1);
            }}
            className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
          >
            <option value="">All</option>
            <option value="ACTIVE">Active</option>
            <option value="PENDING">Pending</option>
            <option value="SUSPENDED">Suspended</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
          Type
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value as UserTypeValue | '');
              setPage(1);
            }}
            className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none"
          >
            <option value="">All</option>
            <option value="USER">User</option>
            <option value="ADMIN">Admin</option>
            <option value="DEV_USER">Dev user</option>
          </select>
        </label>
      </div>

      {error && <p className="text-white/80">{error}</p>}

      <div
        aria-busy={loading}
        className={`overflow-x-auto rounded-xl border border-white/15 bg-black/20 transition-opacity ${
          loading ? 'opacity-60' : 'opacity-100'
        }`}
      >
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-white/55">
              <Th>User</Th>
              <Th>Status</Th>
              <Th>Type</Th>
              <Th>Enabled</Th>
              <Th>Joined</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-white/50">
                  Loading users…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-white/50">
                  No users match.
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.userId} className="border-b border-white/5 align-top last:border-0 text-white/85">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => onSelect(u.userId)}
                      className="text-left transition hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    >
                      <div className="font-medium text-white">{u.displayName || '—'}</div>
                      <div className="text-xs text-white/50 [overflow-wrap:anywhere]">{u.email}</div>
                    </button>
                    {!u.enabled && u.disabledReason && (
                      <div className="mt-1 text-[11px] text-[#ff8da6]">
                        Disabled: {u.disabledReason}
                        {u.disabledByEmail ? ` (by ${u.disabledByEmail})` : ''}
                      </div>
                    )}
                    {!u.canEmail && <UndeliverableBadge user={u} />}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={u.status} />
                  </td>
                  <td className="px-4 py-3">
                    <TypeBadge type={u.type} />
                  </td>
                  <td className="px-4 py-3">
                    {u.enabled ? (
                      <span className="text-white/70">Yes</span>
                    ) : (
                      <span className="text-[#ff8da6]">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white/60">
                    {u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-US') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      {actionsFor(u, u.userId === selfId).map((a) => (
                        <RowAction
                          key={a}
                          tone={ACTION_META[a].tone}
                          title={ACTION_META[a].hint}
                          onClick={() => setPending({ user: u, action: a })}
                        >
                          {ACTION_META[a].verb}
                        </RowAction>
                      ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs leading-relaxed text-white/45">
        <span className="font-medium text-[#ff8da6]">Disable</span> blocks sign-in entirely and
        revokes their sessions (reverse with{' '}
        <span className="font-medium text-white/70">Enable</span>).{' '}
        <span className="font-medium text-[#ff8da6]">Suspend</span> leaves sign-in working but
        blocks gameplay and hides them from the leaderboard (reverse with{' '}
        <span className="font-medium text-white/70">Reinstate</span>). Both require a reason and
        are recorded in the audit log.
      </p>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        disabled={loading}
        itemLabel="users"
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />

      {pending && (
        <ActionModal
          user={pending.user}
          action={pending.action}
          onClose={() => setPending(null)}
          onDone={() => {
            setPending(null);
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}

/** Which actions to offer for a row. Self-targeting destructive actions are
 *  omitted (the server blocks them anyway). */
function actionsFor(u: AdminUserRow, isSelf: boolean): UserAction[] {
  const a: UserAction[] = [];
  if (u.enabled) {
    if (!isSelf) a.push('disable');
    if (u.status === 'ACTIVE') {
      if (!isSelf) a.push('suspend');
    } else if (u.status === 'SUSPENDED') {
      a.push('reinstate');
    }
    if (u.type === 'USER' && u.promotable) a.push('promote');
  } else {
    a.push('enable');
  }
  if (u.type === 'ADMIN' && !isSelf) a.push('demote');
  return a;
}

function ActionModal({
  user,
  action,
  onClose,
  onDone,
}: {
  user: AdminUserRow;
  action: UserAction;
  onClose: () => void;
  onDone: () => void;
}) {
  const meta = ACTION_META[action];
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstRef = useRef<HTMLTextAreaElement | HTMLButtonElement>(null);

  useEffect(() => {
    firstRef.current?.focus();
  }, []);

  const reasonOk = !meta.needsReason || reason.trim().length >= 3;

  const confirm = async (): Promise<void> => {
    if (!reasonOk) return;
    setBusy(true);
    setError(null);
    try {
      await userAction(user.userId, action, meta.needsReason ? reason.trim() : undefined);
      onDone();
    } catch (err) {
      setBusy(false);
      setError(err instanceof ApiError ? err.message : 'Action failed. Please try again.');
    }
  };

  return (
    <Modal title={meta.title} onClose={onClose} disabled={busy}>
      <p className="mt-3 text-sm text-white/80">
        {meta.verb} <span className="text-white">{user.displayName || user.email}</span>
        {action === 'promote' && ' — they will gain admin portal access.'}
        {action === 'demote' && ' — they will lose admin portal access and be signed out.'}
        {action === 'disable' && ' — they will be signed out and blocked from logging in.'}
        {action === 'suspend' && ' — they can still log in but cannot play.'}
        {(action === 'enable' || action === 'reinstate') && '?'}
      </p>

      {meta.needsReason && (
          <label className="mt-4 flex flex-col gap-1 text-xs uppercase tracking-wide text-white/55">
            Reason
            <textarea
              ref={firstRef as RefObject<HTMLTextAreaElement>}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={255}
              className="rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm normal-case tracking-normal text-white placeholder:text-white/35 focus:border-white/40 focus:outline-none"
              placeholder="Recorded in the audit log"
            />
          </label>
        )}

        {error && <p className="mt-3 text-sm text-[#ff8da6]">{error}</p>}

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-full border border-white/25 px-5 py-2 text-sm text-white transition hover:bg-white/10 disabled:opacity-50 sm:min-w-[120px]"
          >
            Cancel
          </button>
          <button
            ref={!meta.needsReason ? (firstRef as RefObject<HTMLButtonElement>) : undefined}
            type="button"
            onClick={() => void confirm()}
            disabled={busy || !reasonOk}
            className={[
              'rounded-full px-5 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[160px]',
              meta.tone === 'danger'
                ? 'bg-[#c0344f] text-white hover:bg-[#d83c5a]'
                : meta.tone === 'accent'
                  ? 'bg-[#00ff8b] text-[#10231a] hover:bg-[#5bffb4]'
                  : 'bg-white/90 text-[#2a2237] hover:bg-white',
            ].join(' ')}
          >
            {busy ? 'Working…' : meta.verb}
        </button>
      </div>
    </Modal>
  );
}

function Th({ children }: { children: ReactNode }) {
  return <th className="px-4 py-2 font-heading text-xs uppercase tracking-wide">{children}</th>;
}

/** Email suppressed by the SNS bounce/complaint handler (canEmail=false).
 *  Same amber pill as the detail view; reason + date on hover. */
function UndeliverableBadge({ user }: { user: AdminUserRow }) {
  const detail = [
    user.canEmailReason,
    user.canEmailAt ? `since ${new Date(user.canEmailAt).toLocaleDateString('en-US')}` : null,
  ]
    .filter(Boolean)
    .join(', ');
  return (
    <div className="mt-1">
      <span
        title={detail || undefined}
        className="rounded bg-[#3a2a0e] px-2 py-0.5 text-[11px] text-[#ffd17a]"
      >
        Email undeliverable
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: UserStatusValue }) {
  const map: Record<UserStatusValue, string> = {
    ACTIVE: 'bg-[#0f3d2a] text-[#5bffb4]',
    PENDING: 'bg-[#3d360f] text-[#ffd95b]',
    SUSPENDED: 'bg-[#3d240f] text-[#ffae5b]',
  };
  return <span className={`rounded px-2 py-0.5 text-xs ${map[status]}`}>{status}</span>;
}

function TypeBadge({ type }: { type: UserTypeValue }) {
  const map: Record<UserTypeValue, string> = {
    USER: 'bg-white/10 text-white/70',
    ADMIN: 'bg-[#2a1140] text-[#d08dff]',
    DEV_USER: 'bg-white/5 text-white/45',
  };
  const label = type === 'DEV_USER' ? 'DEV' : type;
  return <span className={`rounded px-2 py-0.5 text-xs ${map[type]}`}>{label}</span>;
}

function RowAction({
  children,
  tone,
  title,
  onClick,
}: {
  children: ReactNode;
  tone: 'danger' | 'accent' | 'neutral';
  title?: string;
  onClick: () => void;
}) {
  const toneClass =
    tone === 'danger'
      ? 'border-[#c0344f]/50 text-[#ff8da6] hover:bg-[#c0344f]/15'
      : tone === 'accent'
        ? 'border-[#00ff8b]/40 text-[#5bffb4] hover:bg-[#00ff8b]/10'
        : 'border-white/25 text-white/80 hover:bg-white/10';
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`rounded-full border px-3 py-1 text-xs transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${toneClass}`}
    >
      {children}
    </button>
  );
}

